import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores'
import { message } from 'ant-design-vue'
import { preloadComponent, AdminComponents, AuthComponents, LayoutComponents, DashboardComponents } from '@/utils/lazyLoad'
import { setupRouterGuards } from './guards'
import { permissionManager } from '@/utils/permission'
import { PermissionCode } from '@/constants/permission-codes'
import type { RouteMeta } from '@/types/permission'
import { getModuleRoutes, getUserRoutes, getSystemRoutes } from './modules'

// 导入模块路由
import authRoutes from './modules/auth'
import projectRoutes from './modules/project'
import videoRoutes from './modules/video'
import taskRoutes from './modules/task'
import documentRoutes from './modules/document'
import systemRoutes from './modules/system'
import messageRoutes from './modules/message'
import speechTranslationRoutes, { translationRecordRoute } from './modules/speech-translation'
import { crV3ViewerRoute } from './modules/cr-v3-viewer'
import { crV3H5Route } from './modules/cr-v3-h5'
import { crV3JoinRoute } from './modules/cr-v3-join'
import crRoutes from './modules/cr'
import crV3Routes from './modules/cr-v3'
import demoRoutes from './modules/demo'
import teachingRoutes from './modules/teaching'

/** Demo 路由：开发/UAT 默认可用，生产需 VITE_ENABLE_DEMO=true */
const isDemoEnabled = !import.meta.env.PROD || import.meta.env.VITE_ENABLE_DEMO === 'true'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  // 认证路由（不需要布局）
  ...authRoutes,
  // 观众页（不需认证，无布局）
  translationRecordRoute,
  crV3JoinRoute,
  crV3ViewerRoute,
  crV3H5Route,
  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('@/views/error/403.vue'),
    meta: {
      title: '无权限访问',
      requiresAuth: false,
      hidden: true
    }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: {
      title: '页面不存在',
      requiresAuth: false,
      hidden: true
    }
  },
  // 旧文档翻译 SPA 路径（/document/* 已留给本地 file_key 直链，由 Nginx 反代后端）
  {
    path: '/document/upload',
    redirect: (to) => ({ path: '/doc-workflow/upload', query: to.query, hash: to.hash })
  },
  {
    path: '/document/translation/:id',
    redirect: (to) => ({
      path: `/doc-workflow/translation/${to.params.id}`,
      query: to.query,
      hash: to.hash
    })
  },
  {
    path: '/document/results/:id',
    redirect: (to) => ({
      path: `/doc-workflow/results/${to.params.id}`,
      query: to.query,
      hash: to.hash
    })
  },
  {
    path: '/research/messages',
    redirect: (to) => ({ path: '/profile/messages', query: to.query, hash: to.hash })
  },
  {
    path: '/research/upload',
    redirect: (to) => ({ path: '/doc-workflow/upload', query: to.query, hash: to.hash })
  },
  {
    path: '/research/lesson/:pathMatch(.*)*',
    redirect: (to) => {
      const rest = to.params.pathMatch
      const suffix = Array.isArray(rest) ? rest.join('/') : (rest || '')
      return {
        path: suffix ? `/teaching/lesson/${suffix}` : '/teaching/lesson',
        query: to.query,
        hash: to.hash
      }
    }
  },
  {
    path: '/research/knowledge',
    redirect: (to) => ({ path: '/teaching/knowledge', query: to.query, hash: to.hash })
  },
  {
    path: '/research/graph',
    redirect: (to) => ({ path: '/teaching/graph', query: to.query, hash: to.hash })
  },
  {
    path: '/research',
    redirect: '/dashboard'
  },
  {
    path: '/',
    component: LayoutComponents.MainLayout,
    meta: {
      requiresAuth: true
    },
    children: [
      // 工作台（壳层，仅需登录）
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: DashboardComponents.DashboardView,
        meta: {
          titleKey: 'menu.dashboard',
          title: '工作台',
          requiresAuth: true,
          icon: 'DashboardOutlined',
          breadcrumb: true
        }
      },
      // 模块化路由
      ...projectRoutes,
      ...videoRoutes,
      ...taskRoutes,
      ...documentRoutes,
      ...teachingRoutes,
      ...messageRoutes,
      ...speechTranslationRoutes,
      ...crRoutes,
      ...crV3Routes,
      ...(isDemoEnabled ? demoRoutes : []),
      // 测试页面（开发环境）
      {
        path: 'test',
        name: 'Test',
        meta: {
          title: '测试页面',
          requiresAuth: true,
          permissions: [PermissionCode.SYSTEM_USER],
          icon: 'ExperimentOutlined',
          hidden: import.meta.env.PROD // 生产环境隐藏
        },
        children: []
      },
      // 个人中心（壳层，仅需登录）
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/components/layout/RouteOutlet.vue'),
        redirect: { name: 'PersonalInfo' },
        meta: {
          titleKey: 'menu.profile',
          title: '个人中心',
          requiresAuth: true,
          icon: 'UserOutlined'
        },
        children: [
          ...getUserRoutes()
        ]
      },
      // 系统管理置于菜单末尾
      ...systemRoutes,
    ]
  },
  // 通配符路由，必须放在最后
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

/**
 * 生成面包屑导航
 * @param route 当前路由
 * @returns 面包屑数组
 */
export function generateBreadcrumbs(route: any) {
  const breadcrumbs: Array<{ title: string; path?: string }> = []
  const matched = route.matched.filter((item: any) => item.meta?.breadcrumb !== false)
  
  matched.forEach((item: any, index: number) => {
    if (item.meta?.title) {
      breadcrumbs.push({
        title: item.meta.title,
        path: index === matched.length - 1 ? undefined : item.path
      })
    }
  })
  
  return breadcrumbs
}

/**
 * 根据 meta.permissions 过滤菜单（与后端 permission code 一致，不再使用 userType/roles）
 */
export function filterMenuByPermission(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  return routes.filter(route => {
    if (route.meta?.hidden) return false
    if (!permissionManager.checkRoutePermission(route)) return false
    if (route.children) {
      route.children = filterMenuByPermission(route.children)
    }
    return true
  })
}

// 设置路由守卫
setupRouterGuards(router)

export default router